import React, { useState, useEffect, useRef } from 'react';
import { X, CreditCard, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { useTokens } from '../../hooks/useTokens';
import { getUserTokenBalance, clearTokenCache } from '../../services/tokenService';
import PostRechargeModal from './PostRechargeModal';

interface DirectCheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  checkoutUrl: string;
  purchaseId: string;
  tokenAmount: number;
  onPaymentSuccess: () => void;
  preferenceId?: string; // ID da preferência do Mercado Pago
}

const DirectCheckout: React.FC<DirectCheckoutProps> = ({
  isOpen,
  onClose,
  checkoutUrl,
  purchaseId,
  tokenAmount,
  onPaymentSuccess,
  preferenceId
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'checking' | 'success' | 'failed'>('pending');
  const [statusMessage, setStatusMessage] = useState('');
  const [showPostRechargeModal, setShowPostRechargeModal] = useState(false);
  const [creditedTokens, setCreditedTokens] = useState(0);
  const [pendingReferralTopup, setPendingReferralTopup] = useState<{ tokens: number; paymentId?: string } | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initialBalanceRef = useRef<number | null>(null);
  const statusPollingRef = useRef<NodeJS.Timeout | null>(null);
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { user } = useAuth();
  const { refreshData: refreshTokens } = useTokens();
  const successHandledRef = useRef(false);
  const lastStatusRef = useRef<string>('');
  const setStatusSafe = (next: string) => {
    if (lastStatusRef.current !== next) {
      lastStatusRef.current = next;
      setPaymentStatus(next as any);
    }
  };
  const setMessageSafe = (msg: string) => {
    setStatusMessage(prev => (prev === msg ? prev : msg));
  };
  const handleApprovedOnce = async () => {
    if (successHandledRef.current) return;
    successHandledRef.current = true;
    try {
      await refreshTokens();
      try { window.dispatchEvent(new CustomEvent('tokenBalanceUpdated')); } catch { }
    } catch { }
    setPaymentStatus('success');
    setStatusMessage('✅ Pagamento aprovado! Tokens creditados com sucesso!');
    if (!showPostRechargeModal) {
      setShowPostRechargeModal(true);
      setCreditedTokens(tokenAmount);
    }
    try { onPaymentSuccess(); } catch { }
    setTimeout(() => {
      onClose();
    }, 1200);
  };


  // Função para inicializar o saldo inicial
  const initializeBalance = async () => {
    if (!user?.id) return;

    try {
      clearTokenCache();
      const currentBalance = await getUserTokenBalance(user.id);
      initialBalanceRef.current = currentBalance;
      console.log('🔵 [POLLING] Saldo inicial capturado:', currentBalance);
      setStatusMessage(`Saldo inicial: ${currentBalance} tokens`);
    } catch (error) {
      console.error('❌ [POLLING] Erro ao capturar saldo inicial:', error);
      setStatusMessage('Erro ao inicializar polling');
    }
  };

  // Função para iniciar o polling
  const startTokenPolling = () => {
    if (!user?.id || initialBalanceRef.current === null) {
      console.log('⚠️ [POLLING] Condições não atendidas - user:', !!user?.id, 'initialBalance:', initialBalanceRef.current);
      return;
    }

    console.log('🔄 [POLLING] Iniciando polling de tokens...');
    setStatusMessage('Aguardando pagamento...');

    pollingIntervalRef.current = setInterval(async () => {
      await checkTokenBalanceChange();
    }, 10000); // Verificar a cada 10 segundos (reduz egress)
  };

  // Função para verificar mudanças no saldo de tokens
  const checkTokenBalanceChange = async () => {
    if (!user?.id || initialBalanceRef.current === null) return;

    try {
      clearTokenCache();
      const currentBalance = await getUserTokenBalance(user.id);
      console.log('🔍 [POLLING] Verificando saldo - Inicial:', initialBalanceRef.current, 'Atual:', currentBalance);

      // Se o saldo aumentou, significa que tokens foram creditados
      if (currentBalance > initialBalanceRef.current) {
        const creditedTokens = currentBalance - initialBalanceRef.current;
        console.log('✅ [POLLING] MUDANÇA DETECTADA! Tokens creditados:', creditedTokens);
        setPendingReferralTopup({ tokens: creditedTokens, paymentId: purchaseId || preferenceId });
        stopTokenPolling();
        await handleApprovedOnce();
        setStatusMessage(`Pagamento aprovado! ${tokenAmount} tokens creditados.`);
      }
    } catch (error) {
      console.error('❌ [POLLING] Erro ao verificar saldo:', error);
    }
  };

  // Função para parar o polling
  const stopTokenPolling = () => {
    if (pollingIntervalRef.current) {
      console.log('🛑 [POLLING] Parando polling de tokens');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Função para obter a URL de verificação baseada no ambiente
  const getVerifyUrl = () => {
    // Em desenvolvimento, usar o proxy do Vite
    if (import.meta.env?.DEV || window.location.hostname === 'localhost') {
      return '/api/tokens/verify'
    }
    // Em produção, chamar diretamente a Cloud Function
    return 'https://us-central1-overpixel-hub.cloudfunctions.net/verifyTokensPayment'
  }

  // Função para verificar status do pagamento via webhook/API
  const checkPaymentStatus = async () => {
    try {
      setMessageSafe('Verificando status do pagamento...');

      // Verificar usando purchase_id ou preference_id
      const queryParam = preferenceId ? `preference_id=${preferenceId}` : `purchase_id=${purchaseId}`;
      const verifyUrl = getVerifyUrl();
      console.log('🔍 URL de verificação:', verifyUrl);
      const response = await fetch(`${verifyUrl}?${queryParam}`);
      const data = await response.json();

      console.log('🔍 Status do pagamento:', data);

      if (data.success && (data.status === 'approved' || data.payment_status === 'approved' || data.status_detail === 'accredited')) {
        await handleApprovedOnce();
      } else if (data.status === 'rejected' || data.status === 'cancelled' || data.payment_status === 'rejected') {
        setStatusSafe('failed');
        setMessageSafe('❌ Pagamento não foi aprovado. Tente novamente.');
      } else if (data.status === 'pending' || data.payment_status === 'pending') {
        setStatusSafe('pending');
        setMessageSafe('⏳ Pagamento ainda está sendo processado...');
      } else {
        setStatusSafe('pending');
        setMessageSafe('');
      }
    } catch (error) {
      console.error('Erro ao verificar status do pagamento:', error);
      setStatusSafe('failed');
      setMessageSafe('❌ Erro ao verificar pagamento. Tente novamente.');
    }
  };

  const startStatusPolling = () => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
    }
    statusPollingRef.current = setInterval(() => {
      if (paymentStatus !== 'success' && paymentStatus !== 'failed') {
        checkPaymentStatus();
      }
    }, 2000);
  };

  const stopStatusPolling = () => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  };

  // Listener para mensagens do iframe (Mercado Pago)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verificar origem por segurança
      if (!event.origin.includes('mercadopago') && !event.origin.includes('mercadolibre')) {
        return;
      }

      const data = event.data;

      // Mercado Pago envia diferentes tipos de mensagens
      if (data.type === 'payment_result' || data.status) {
        if (data.status === 'approved' || data.type === 'success') {
          checkPaymentStatus();
        } else if (data.status === 'rejected' || data.status === 'cancelled') {
          setPaymentStatus('failed');
          setStatusMessage('Pagamento cancelado ou rejeitado.');
        }
      }

      // Detectar redirecionamento ou fechamento
      if (data.type === 'redirect' || data.action === 'close') {
        // Aguardar um pouco e verificar status
        checkTimeoutRef.current = setTimeout(() => {
          checkPaymentStatus();
        }, 2000);
      }
    };

    if (isOpen) {
      window.addEventListener('message', handleMessage);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [isOpen, purchaseId]);

  // Detectar quando o usuário volta para a página (visibilitychange) - OTIMIZADO
  useEffect(() => {
    let visibilityTimeout: NodeJS.Timeout | null = null;

    const handleVisibilityChange = () => {
      // Limpar timeout anterior se existir
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
        visibilityTimeout = null;
      }

      // Só verificar se modal estiver aberto, status pendente e usuário voltou para a aba
      if (!document.hidden && isOpen && paymentStatus === 'pending') {
        // Debounce para evitar múltiplas verificações
        visibilityTimeout = setTimeout(() => {
          checkPaymentStatus();
          visibilityTimeout = null;
        }, 2000); // Aumentado para 2 segundos
      }
    };

    if (isOpen && paymentStatus === 'pending') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
    };
  }, [isOpen, paymentStatus]);

  // useEffect para inicializar saldo quando modal abre
  useEffect(() => {
    if (isOpen && user?.id) {
      console.log('🚀 [POLLING] Modal aberto, inicializando saldo do usuário:', user.id);
      initializeBalance();
    }
  }, [isOpen, user?.id]);

  useEffect(() => {
    if (isOpen && (purchaseId || preferenceId) && !isLoading) {
      const startId = setTimeout(() => {
        startStatusPolling();
      }, 3000);
      return () => {
        clearTimeout(startId);
        stopStatusPolling();
      };
    }
  }, [isOpen, purchaseId, preferenceId, isLoading]);

  // useEffect para iniciar polling após iframe carregar
  useEffect(() => {
    if (isOpen && !isLoading && initialBalanceRef.current !== null) {
      const startPollingTimeout = setTimeout(() => {
        startTokenPolling();
      }, 1000);

      return () => {
        clearTimeout(startPollingTimeout);
      };
    }
  }, [isOpen, isLoading, user?.id]);

  // useEffect para cleanup quando modal fecha
  useEffect(() => {
    if (!isOpen) {
      console.log('🧹 [POLLING] Modal fechado, fazendo cleanup');
      stopTokenPolling();
      stopStatusPolling();
      if (statusTimeoutRef.current) { clearTimeout(statusTimeoutRef.current); statusTimeoutRef.current = null; }
      initialBalanceRef.current = null;
      // NÃO fechar automaticamente o PostRechargeModal aqui
      // Deixar o usuário fechar manualmente
      // setShowPostRechargeModal(false);
      // setCreditedTokens(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (paymentStatus === 'success' || paymentStatus === 'failed') {
      stopStatusPolling();
      if (statusTimeoutRef.current) { clearTimeout(statusTimeoutRef.current); statusTimeoutRef.current = null; }
    }
  }, [paymentStatus]);

  // useEffect para timeout de segurança do polling
  useEffect(() => {
    if (isOpen && pollingIntervalRef.current) {
      console.log('⏱️ [POLLING] Configurando timeout de segurança (5 minutos)');
      const timeoutId = setTimeout(() => {
        console.log('⚠️ [POLLING] Timeout de segurança atingido, parando polling');
        stopTokenPolling();
      }, 5 * 60 * 1000); // 5 minutos

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, pollingIntervalRef.current]);

  // Função para fechar modal e chamar onPaymentSuccess
  const handlePostRechargeClose = () => {
    console.log('🔄 [MODAL] Fechando PostRechargeModal');
    setPendingReferralTopup(null);
    setShowPostRechargeModal(false);
    setCreditedTokens(0);
  };

  // Removido modal local - usando componente importado

  // Funcionalidade para fechar com ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleClose = () => {
    // Limpar timeouts
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
      checkTimeoutRef.current = null;
    }

    // Parar polling
    stopTokenPolling();
    stopStatusPolling();
    if (statusTimeoutRef.current) { clearTimeout(statusTimeoutRef.current); statusTimeoutRef.current = null; }

    // Reset states
    setPaymentStatus('pending');
    setStatusMessage('');
    setIsLoading(true);
    setShowPostRechargeModal(false);
    setCreditedTokens(0);
    initialBalanceRef.current = null;
    onClose();
  };

  // Função para fechar ao clicar fora do modal
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  // Botão para verificar status manualmente
  const handleManualCheck = () => {
    if (paymentStatus !== 'checking') {
      checkPaymentStatus();
    }
  };

  if (!isOpen && !showPostRechargeModal) return null;

  return (
    <>
      <PostRechargeModal
        isOpen={showPostRechargeModal}
        creditedTokens={creditedTokens}
        onClose={handlePostRechargeClose}
      />
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleBackdropClick}
        >
          <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col border border-gray-700">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-semibold text-white">Checkout - {tokenAmount} Tokens</h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-300 hover:text-white"
                title="Fechar (ESC)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status do Pagamento */}
            {(paymentStatus !== 'pending' || statusMessage) && (
              <div className={`p-4 text-center border-b border-gray-700 ${paymentStatus === 'success' ? 'bg-green-900/50 text-green-300' :
                  paymentStatus === 'failed' ? 'bg-red-900/50 text-red-300' :
                    paymentStatus === 'checking' ? 'bg-blue-900/50 text-blue-300' :
                      'bg-gray-800 text-gray-300'
                }`}>
                {paymentStatus === 'checking' && (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-medium">{statusMessage}</span>
                  </div>
                )}
                {paymentStatus === 'success' && (
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="font-medium">{statusMessage}</span>
                  </div>
                )}
                {paymentStatus === 'failed' && (
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-400" />
                      <span className="font-medium">{statusMessage}</span>
                    </div>
                    <button
                      onClick={handleManualCheck}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Verificar Status Novamente
                    </button>
                  </div>
                )}
                {paymentStatus === 'pending' && statusMessage && (
                  <div className="flex items-center justify-center gap-2">
                    <span className="font-medium text-blue-300">{statusMessage}</span>
                  </div>
                )}
              </div>
            )}

            {/* Iframe Container */}
            <div className="flex-1 relative">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                    <span className="text-gray-300">Carregando checkout...</span>
                  </div>
                </div>
              )}

              <iframe
                src={checkoutUrl}
                className="w-full h-full border-0"
                onLoad={handleIframeLoad}
                title="Mercado Pago Checkout"
                sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-popups"
              />
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-700 bg-gray-800">
              <div className="text-center">
                <p className="text-sm text-gray-300">🔒 Pagamento seguro processado pelo Mercado Pago</p>
                <p className="text-xs text-gray-400 mt-1">
                  O status será verificado automaticamente após o pagamento
                </p>
              </div>

              {paymentStatus === 'pending' && !isLoading && (
                <div className="mt-3 text-center">
                  <button
                    onClick={handleManualCheck}
                    disabled={paymentStatus === 'checking'}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  >
                    {'Verificar Status do Pagamento'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DirectCheckout;
